import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { handleDifferentUTCOffsets } from "services/adminAutoNotifications/helpers";
import { logInfo } from "services/logging";
import {
  isGroupUser,
  isNewUser,
  isSelfStudyUser,
  isUserStudent,
  isUserTeacher,
  populateUserSession,
} from "services/authUtil";
import Configuration from "./../config";
import NewVersionToast from "./NewVersionToast/NewVersionToast";
import CookieNotification, {
  isCookieNotified,
} from "./legal/CookieNotification/CookieNotification";
import WrongTimezoneWarning from "./WrongTimezoneWarning/WrongTimezoneWarning";
import Toast from "./Toast/Toast";
import TalaeraRoutes from "./routes/TalaeraRoutes";
import { PushNotification } from "./PushNotification/PushNotification";
import { Layout } from "./routes/Layout";
import "./App.css";
import { trackUserId } from "../services/analytics";
import useUser from "hooks/useUser";
import moment from "moment";
import { BROWSER_TAB_ID_KEY } from "utils/storage";
import useHeartbeat from "hooks/useHeartbeat";
import UpdateInProgress from "pages/UpdateInProgress/UpdateInProgress";

const TOAST_TIMEOUT = 5000;
const SERVER_DOWN_VAR_NAME = "talaera_server_down";

export default function App() {
  const [showCookiesNotification, setShowCookiesNotification] = useState(
    !Configuration.COOKIE_NOTIFICATION_DISABLED && !isCookieNotified()
  );
  const [toastMessage, setToastMessage] = useState<null | string>(null);
  const { showWrongTimezoneWarning, setShowWrongTimezoneWarning } = useTimezoneCheck();
  const [authStatusKnown, setAuthStatusKnown] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { apiResponding, pingServer } = useHeartbeat();

  useEffect(() => {
    if (Configuration.USE_MOUSEFLOW) {
      let mf = document.createElement("script");
      mf.type = "text/javascript";
      mf.defer = true;
      mf.src = "//cdn.mouseflow.com/projects/139d0f94-b96a-40de-a7a3-f647ca0e3b0b.js";

      document.getElementsByTagName("head")[0].appendChild(mf);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const pathname = location.pathname;
  const user = useUser();

  // Effectively one-time effect.
  useEffect(() => {
    if (authStatusKnown) {
      return;
    }

    // If request is successfull - user is authenticated; if request is not successful - user is not authenticated.
    // FIXME: second callback (onError) should check for specific response status (403) rather than just assume that any error means
    // that user is not authenticated.
    populateUserSession(
      () => setAuthStatusKnown(true),
      () => setAuthStatusKnown(true)
    );
  }, [authStatusKnown, setAuthStatusKnown]);

  useEffect(() => {
    // We want to make sure that no stale 'server is down/updating' variable
    // exists, to prevent accidental rendering of the UpdateInProgress component.
    if (localStorage.getItem(SERVER_DOWN_VAR_NAME) !== null) {
      localStorage.removeItem(SERVER_DOWN_VAR_NAME);
    }

    setTimeout(saveBrowserTabId, 1000);
  }, [user?.id, navigate]);

  const userId = user?.id;
  useEffect(() => {
    if (userId) {
      trackUserId(userId);
    }
  }, [userId]);

  useEffect(() => {
    if (pathname.startsWith(TalaeraRoutes.LIBRARY_ROOT.path) && isNewUser()) {
      setShowWrongTimezoneWarning(false);
    }
  }, [user, pathname, navigate, setShowWrongTimezoneWarning]);

  const closeWrongTimezoneWarning = () => {
    setShowWrongTimezoneWarning(false);
  };

  const saveBrowserTabId = () => {
    if (!sessionStorage) {
      return;
    }
    const currentTabId = Number(sessionStorage.getItem(BROWSER_TAB_ID_KEY));
    const newTabId = Math.round(Math.random() * 1000);
    if (currentTabId !== newTabId) {
      logInfo(
        "opened new browser tab or refreshed page (old value = " +
          currentTabId +
          ", new value = " +
          newTabId +
          ")",
        {
          from: "App#saveBrowserTabId",
        }
      );
    }
    sessionStorage.setItem(BROWSER_TAB_ID_KEY, String(newTabId));
  };

  const onContinueFromCookieNotification = () => {
    setShowCookiesNotification(false);
  };

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage(null);
    }, TOAST_TIMEOUT);
  };

  const isUserExist = !!user;
  return (
    <div className="App">
      {apiResponding ? (
        <Layout
          isAuthenticated={authStatusKnown ? isUserExist : null}
          showToast={showToast}
        />
      ) : (
        <UpdateInProgress pingServer={pingServer} />
      )}

      <NewVersionToast />
      {isUserExist && showCookiesNotification && (
        <CookieNotification onContinue={onContinueFromCookieNotification} />
      )}
      {showWrongTimezoneWarning && (
        <WrongTimezoneWarning
          showDialog={showWrongTimezoneWarning}
          onClose={closeWrongTimezoneWarning}
        />
      )}
      {!!toastMessage && (
        <div className="toast-wrapper">
          <Toast
            dataCy="app-toast"
            showToast={toastMessage}
            message={toastMessage}
            timeout={TOAST_TIMEOUT}
          />
        </div>
      )}
      <PushNotification />
    </div>
  );
}

function useTimezoneCheck() {
  const [showWrongTimezoneWarning, setShowWrongTimezoneWarning] = useState(false);
  const [warningShown, setWarningShown] = useState(false);
  const currentUser = useUser();

  useEffect(() => {
    const userTimezone = currentUser?.profile.timezone.id;
    const date = new Date();
    const isDifferentTimezone =
      userTimezone &&
      moment.tz(date, userTimezone).format("Z") !== moment(date).format("Z");

    // We will have it at least for China and we show location input in this case,
    // which won't work if there is no window.google or window.google.maps.places.
    // So, we don't show WrongTimezoneWarning if user can't do anything to change it.
    const haveGoogleServices = !window.google || !window.google.maps.places;

    const shouldShowForCurrentUserType =
      (isUserStudent(currentUser) || isUserTeacher(currentUser)) &&
      !isNewUser() &&
      !isSelfStudyUser(currentUser) &&
      !isGroupUser(currentUser);

    if (
      isDifferentTimezone &&
      !warningShown &&
      haveGoogleServices &&
      shouldShowForCurrentUserType
    ) {
      handleDifferentUTCOffsets(currentUser);
      setShowWrongTimezoneWarning(true);
      setWarningShown(true);
    }
  }, [currentUser, warningShown]);

  return { showWrongTimezoneWarning, setShowWrongTimezoneWarning };
}
